<template>
	<div class="content">
		<base-header class="pb-6">
			<div class="row align-items-center py-4">
				<div class="col-lg-6 col-7">
					<h6 class="h2 text-white d-inline-block mb-0">{{ $t("pos.menu.devices") }}</h6>
					<nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
						<route-bread-crumb></route-bread-crumb>
					</nav>
				</div>
				<div class="col-lg-6 col-5 text-right">
					<!-- owner request: QR to download the POS app (everyone who sees this page) -->
					<base-button v-if="canSeeApk" size="md" type="neutral" class="pos-apk-btn" @click="apkVisible = true">{{ $t("pos.device.apk") }}</base-button>
					<base-button v-if="isAdmin" size="md" type="neutral" @click="openForm(null)">{{ $t("pos.device.add") }}</base-button>
				</div>
			</div>
		</base-header>

		<div class="container-fluid mt--6">
			<pos-install-alert :show="notInstalled"></pos-install-alert>
			<!-- the table is the whole card: no padding at all (no strip above the header);
			     pos-table-card clips the header corners to the card radius -->
			<card class="no-border-card pos-table-card" body-classes="p-0">
				<el-table :data="rows" v-loading="loading" header-row-class-name="thead-light" class="bon-table pos-table">
					<el-table-column :label="$t('pos.device.code')" prop="code" width="110"></el-table-column>
					<el-table-column :label="$t('pos.device.name')" prop="name" min-width="160"></el-table-column>
					<el-table-column :label="$t('pos.device.status')" width="120">
						<template slot-scope="{ row }">
							<el-tag :type="row.status === 1 ? 'success' : 'info'" size="small">{{ row.status === 1 ? $t("pos.device.enabled") : $t("pos.device.disabled") }}</el-tag>
						</template>
					</el-table-column>
					<el-table-column :label="$t('pos.device.paired')" width="175" class-name="pos-nowrap">
						<template slot-scope="{ row }">{{ row.paired ? row.paired_at : $t("pos.device.not_paired") }}</template>
					</el-table-column>
					<el-table-column :label="$t('pos.device.mode')" width="190">
						<template slot-scope="{ row }">
							<el-tag v-if="row.session_mode === 'device'" type="primary" size="small" class="pos-tag-wrap">{{ $t("pos.device.mode_app") }}</el-tag>
							<span v-else>{{ $t("pos.device.mode_web") }}</span>
						</template>
					</el-table-column>
					<el-table-column :label="$t('pos.device.session_user')" min-width="130">
						<template slot-scope="{ row }">{{ row.session_user ? row.session_user.name : "" }}</template>
					</el-table-column>
					<el-table-column :label="$t('pos.device.passcode')" width="190">
						<template slot-scope="{ row }">
							<el-tag v-if="row.passcode_state === 'hard_locked'" type="danger" size="small" class="pos-tag-wrap">{{ $t(passcodeKey(row.passcode_state)) }}</el-tag>
							<el-tag v-else-if="row.passcode_state === 'cooldown'" type="warning" size="small" class="pos-tag-wrap">{{ $t(passcodeKey(row.passcode_state)) }}</el-tag>
							<span v-else>{{ $t(passcodeKey(row.passcode_state)) }}</span>
						</template>
					</el-table-column>
					<el-table-column :label="$t('pos.device.lock_idle_col')" width="130">
						<template slot-scope="{ row }">{{ $t(lockIdleKey(row.lock_idle_seconds)) }}</template>
					</el-table-column>
					<el-table-column :label="$t('pos.device.app_version')" width="120">
						<template slot-scope="{ row }">{{ row.app_version || "" }}</template>
					</el-table-column>
					<el-table-column :label="$t('pos.device.last_seen')" prop="last_seen_at" width="175" class-name="pos-nowrap"></el-table-column>
					<el-table-column :label="$t('pos.device.last_user')" prop="last_user_name" min-width="140"></el-table-column>
					<el-table-column :label="$t('pos.device.is_test')" width="130" align="center" label-class-name="pos-nowrap">
						<template slot-scope="{ row }">{{ row.is_test ? "✓" : "" }}</template>
					</el-table-column>
					<el-table-column :label="$t('pos.device.bills')" prop="bill_count" width="80" align="right"></el-table-column>
					<!-- row menu pinned at the right edge, same as /backoffice/orders-overview
					     (OrdersNew.vue): the popper is appended to <body>, so it opens from the
					     fixed-right copy too -->
					<el-table-column :label="$t('actions')" width="64" fixed="right" align="center" class-name="bon-tight">
						<template slot-scope="{ row }">
							<el-dropdown trigger="click" placement="bottom-end" @command="(c) => onCommand(c, row)">
								<base-button size="sm" type="secondary" icon data-testid="pos-row-menu" :aria-label="$t('pos.common.menu')" :title="$t('pos.common.menu')">
									<i class="fa fa-ellipsis-v"></i>
								</base-button>
								<el-dropdown-menu slot="dropdown" class="pos-row-menu">
									<template v-if="isAdmin">
										<el-dropdown-item command="edit"><i class="fa fa-pen text-blue mr-2"></i>{{ $t("pos.device.edit") }}</el-dropdown-item>
										<el-dropdown-item command="pair" :disabled="row.status !== 1"><i class="fa fa-qrcode text-teal mr-2"></i>{{ $t("pos.device.pairing_code_create") }}</el-dropdown-item>
										<el-dropdown-item command="unpair" :disabled="!row.paired"><i class="fa fa-unlink text-warning mr-2"></i>{{ $t("pos.device.unpair") }}</el-dropdown-item>
										<el-dropdown-item command="defaults"><i class="fa fa-boxes text-success mr-2"></i>{{ $t("pos.device.default_set") }}</el-dropdown-item>
										<el-dropdown-item command="staff"><i class="fa fa-users text-purple mr-2"></i>{{ $t("pos.device.staff") }}</el-dropdown-item>
									</template>
									<!-- phase 3 R-X: supervisors see only this item -->
									<el-dropdown-item command="passcode" :disabled="row.session_mode !== 'device'"><i class="fa fa-key text-warning mr-2"></i>{{ $t("pos.device.passcode_reset") }}</el-dropdown-item>
									<el-dropdown-item v-if="isAdmin" command="delete" :disabled="row.bill_count > 0" divided><span class="text-danger"><i class="fa fa-trash mr-2"></i>{{ $t("pos.device.delete") }}</span></el-dropdown-item>
								</el-dropdown-menu>
							</el-dropdown>
						</template>
					</el-table-column>
				</el-table>
			</card>
		</div>

		<!-- device form -->
		<el-dialog :title="form.id ? $t('pos.device.form_title_edit') : $t('pos.device.form_title_add')" :visible.sync="formVisible" width="480px">
			<base-input :label="$t('pos.device.code') + ' *'" :value="form.code" :disabled="form.bill_count > 0" maxlength="10" @input="(v) => (form.code = normCode(v))"></base-input>
			<small v-if="form.bill_count > 0" class="text-muted d-block mt--3 mb-3">{{ $t("pos.device.code_locked_hint") }}</small>
			<base-input :label="$t('pos.device.name') + ' *'" v-model="form.name" maxlength="100"></base-input>
			<div class="form-group">
				<label class="form-control-label d-block">{{ $t("pos.device.status") }}</label>
				<el-radio-group v-model="form.status">
					<el-radio :label="1">{{ $t("pos.device.enabled") }}</el-radio>
					<el-radio :label="0">{{ $t("pos.device.disabled") }}</el-radio>
				</el-radio-group>
			</div>
			<el-checkbox v-model="form.is_test" class="mb-3">{{ $t("pos.device.is_test_form") }}</el-checkbox>
			<div class="form-group">
				<label class="form-control-label d-block">{{ $t("pos.device.lock_idle") }}</label>
				<!-- §14: 5 / 10 / 15 minutes or never -->
				<el-select v-model="form.lock_idle_seconds" class="w-100">
					<el-option v-for="v in lockIdleOptions" :key="v" :value="v" :label="$t(lockIdleKey(v))"></el-option>
				</el-select>
				<small class="text-muted">{{ $t("pos.device.lock_idle_hint") }}</small>
			</div>
			<div class="form-group">
				<label class="form-control-label">{{ $t("pos.device.note") }}</label>
				<el-input type="textarea" :rows="2" v-model="form.note" maxlength="255"></el-input>
			</div>
			<span slot="footer">
				<base-button type="secondary" @click="formVisible = false">{{ $t("pos.common.cancel") }}</base-button>
				<base-button type="primary" :disabled="saving || !formValid" @click="save">{{ $t("pos.common.save") }}</base-button>
			</span>
		</el-dialog>

		<!-- pairing code -->
		<el-dialog :title="$t('pos.device.pairing_title')" :visible.sync="pairVisible" width="420px" @close="stopTimer">
			<div class="text-center">
				<div class="pos-pair-code">{{ pairing.code }}</div>
				<div class="mb-3" :class="countdown === '00:00' ? 'text-danger' : 'text-muted'">
					{{ countdown === "00:00" ? $t("pos.device.pairing_expired") : $t("pos.device.pairing_expires", { time: countdown }) }}
				</div>
				<p class="mb-0">{{ $t("pos.device.pairing_hint", { code: pairing.deviceCode }) }}</p>
			</div>
			<span slot="footer">
				<base-button type="primary" @click="pairVisible = false">{{ $t("pos.common.close") }}</base-button>
			</span>
		</el-dialog>

		<!-- default set -->
		<el-dialog :title="$t('pos.device.default_set_title', { code: defaults.deviceCode })" :visible.sync="defaultsVisible" width="680px">
			<pos-product-picker :exclude="defaults.products.map((p) => p.pos_product_id)" @pick="addDefaultProduct" class="mb-2"></pos-product-picker>
			<el-table :data="defaults.products" size="mini" max-height="300" class="mb-3 pos-table">
				<el-table-column type="index" width="40"></el-table-column>
				<el-table-column :label="$t('pos.event.product')" min-width="200">
					<template slot-scope="{ row }">
						{{ row.name }}
						<el-tag v-if="row.status === 0" type="info" size="mini">{{ $t("pos.product.inactive_tag") }}</el-tag>
					</template>
				</el-table-column>
				<el-table-column :label="$t('pos.event.retail_price')" width="110" align="right">
					<template slot-scope="{ row }">{{ money(row.price) }}</template>
				</el-table-column>
				<el-table-column width="130" align="right">
					<template slot-scope="{ $index }">
						<base-button size="sm" type="link" :disabled="$index === 0" @click="move(defaults.products, $index, -1)">↑</base-button>
						<base-button size="sm" type="link" :disabled="$index === defaults.products.length - 1" @click="move(defaults.products, $index, 1)">↓</base-button>
						<base-button size="sm" type="link" class="text-danger" @click="defaults.products.splice($index, 1)">✕</base-button>
					</template>
				</el-table-column>
			</el-table>
			<span slot="footer">
				<base-button type="secondary" @click="defaultsVisible = false">{{ $t("pos.common.cancel") }}</base-button>
				<base-button type="primary" :disabled="saving" @click="saveDefaults">{{ $t("pos.common.save") }}</base-button>
			</span>
		</el-dialog>

		<!-- passcode reset (B45, pos_bo + supervisor) — §14: no input, resets to 123456 -->
		<el-dialog :title="$t('pos.device.apk_title')" :visible.sync="apkVisible" width="420px" custom-class="pos-apk-dialog">
			<div class="text-center">
				<svg class="pos-apk-qr" :viewBox="`0 0 ${apkQrSize} ${apkQrSize}`" shape-rendering="crispEdges" role="img" :aria-label="apkUrl">
					<rect :width="apkQrSize" :height="apkQrSize" fill="#fff" />
					<path :d="apkQrPath" fill="#000" />
				</svg>
			</div>
			<p class="pos-apk-hint mt-3 mb-2">{{ $t("pos.device.apk_hint") }}</p>
			<div class="d-flex align-items-center">
				<code class="pos-apk-url flex-grow-1 mr-2">{{ apkUrl }}</code>
				<base-button size="sm" type="secondary" class="pos-apk-copy" @click="copyApkUrl">{{ $t("pos.device.apk_copy") }}</base-button>
			</div>
			<span slot="footer">
				<base-button type="link" @click="apkVisible = false">{{ $t("pos.common.close") }}</base-button>
			</span>
		</el-dialog>

		<el-dialog :title="$t('pos.device.passcode_reset_title', { code: passcode.deviceCode })" :visible.sync="passcodeVisible" width="420px">
			<p class="mb-0">{{ $t("pos.device.passcode_reset_confirm", { code: passcode.deviceCode }) }}</p>
			<span slot="footer">
				<base-button type="secondary" @click="passcodeVisible = false">{{ $t("pos.common.cancel") }}</base-button>
				<base-button type="primary" :disabled="saving" @click="savePasscode">{{ $t("pos.device.passcode_reset") }}</base-button>
			</span>
		</el-dialog>

		<!-- staff allow-list (§11, admin) -->
		<el-dialog :title="$t('pos.device.staff_title', { code: staff.deviceCode })" :visible.sync="staffVisible" width="520px">
			<el-select v-model="staff.userIds" multiple filterable class="w-100" :placeholder="$t('pos.device.staff_all')">
				<el-option v-for="u in staff.candidates" :key="u.user_id" :label="u.display_name" :value="u.user_id"></el-option>
			</el-select>
			<small class="text-muted d-block mt-2">{{ $t("pos.device.staff_hint") }}</small>
			<span slot="footer">
				<base-button type="secondary" @click="staffVisible = false">{{ $t("pos.common.cancel") }}</base-button>
				<base-button type="primary" :disabled="saving" @click="saveStaff">{{ $t("pos.common.save") }}</base-button>
			</span>
		</el-dialog>
	</div>
</template>

<script>
import swal from "sweetalert2";
import { Table, TableColumn, Tag, Dialog, Dropdown, DropdownMenu, DropdownItem, RadioGroup, Radio, Checkbox, Select, Option, InputNumber, Input } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { PosService } from "@/resource";
import { posMoney, posNormalizeDeviceCode, posValidDeviceCode, posCountdown, posPasscodeStateKey, posLockIdleKey, posLockIdleValid, POS_LOCK_IDLE_OPTIONS } from "@/utils/pos";
import posPageMixin from "./posPageMixin";
import { qrMatrix } from "@/utils/qrcode";
import { posApkUrl } from "@/utils/posApk";
import { copyToClipboard } from "@/utils/clipboard";
import PosInstallAlert from "./PosInstallAlert.vue";
import PosProductPicker from "./PosProductPicker.vue";

const emptyForm = () => ({ id: null, code: "", name: "", status: 1, is_test: false, note: "", bill_count: 0, lock_idle_seconds: 300 });

export default {
	name: "PosDevices",
	mixins: [posPageMixin],
	components: {
		RouteBreadCrumb,
		PosInstallAlert,
		PosProductPicker,
		[Table.name]: Table,
		[TableColumn.name]: TableColumn,
		[Tag.name]: Tag,
		[Dialog.name]: Dialog,
		[Dropdown.name]: Dropdown,
		[DropdownMenu.name]: DropdownMenu,
		[DropdownItem.name]: DropdownItem,
		[RadioGroup.name]: RadioGroup,
		[Radio.name]: Radio,
		[Checkbox.name]: Checkbox,
		[Select.name]: Select,
		[Option.name]: Option,
		[InputNumber.name]: InputNumber,
		[Input.name]: Input
	},
	data() {
		return {
			rows: [],
			saving: false,
			formVisible: false,
			form: emptyForm(),
			pairVisible: false,
			pairing: { code: "", expiresAt: "", deviceCode: "" },
			countdown: "00:00",
			timer: null,
			defaultsVisible: false,
			defaults: { deviceId: null, deviceCode: "", products: [] },
			passcodeVisible: false,
			passcode: { deviceId: null, deviceCode: "" },
			lockIdleOptions: POS_LOCK_IDLE_OPTIONS,
			apkVisible: false,
			staffVisible: false,
			staff: { deviceId: null, deviceCode: "", userIds: [], candidates: [] }
		};
	},
	computed: {
		isAdmin() {
			return !!this.$store.getters["Auth/isPosBOAdmin"]; // §13.1
		},
		// same users as the page itself (route meta requiresPosBOSupervisor)
		canSeeApk() {
			return !!this.$store.getters["Auth/isPosBOSupervisor"];
		},
		apkUrl() {
			return posApkUrl(process.env.VUE_APP_API_BASE_URL);
		},
		apkQr() {
			try {
				return qrMatrix(this.apkUrl, "MEDIUM");
			} catch (e) {
				return null;
			}
		},
		apkQrSize() {
			return this.apkQr ? this.apkQr.length + 8 : 0; // 4-module quiet zone
		},
		apkQrPath() {
			if (!this.apkQr) return "";
			let d = "";
			this.apkQr.forEach((row, y) => {
				row.forEach((dark, x) => {
					if (dark) d += `M${x + 4} ${y + 4}h1v1h-1z`;
				});
			});
			return d;
		},

		formValid() {
			return posValidDeviceCode(this.form.code) && this.form.name.trim() !== "";
		}
	},
	mounted() {
		this.load();
	},
	beforeDestroy() {
		this.stopTimer();
	},
	methods: {
		async copyApkUrl() {
			const ok = await copyToClipboard(this.apkUrl);
			this.$notify({ type: ok ? "success" : "danger", message: this.$t(ok ? "pos.device.apk_copied" : "pos.device.apk_copy_failed") });
		},
		money: posMoney,
		normCode: posNormalizeDeviceCode,
		passcodeKey: posPasscodeStateKey,
		lockIdleKey: posLockIdleKey,
		async load() {
			this.loading = true;
			try {
				const res = await PosService.PosDeviceList();
				this.rows = (res.data && res.data.Data) || [];
				this.notInstalled = false;
			} catch (err) {
				this.rows = [];
				this.posError(err);
			} finally {
				this.loading = false;
			}
		},
		onCommand(cmd, row) {
			if (cmd === "edit") this.openForm(row);
			else if (cmd === "pair") this.pair(row);
			else if (cmd === "unpair") this.unpair(row);
			else if (cmd === "defaults") this.openDefaults(row);
			else if (cmd === "staff") this.openStaff(row);
			else if (cmd === "passcode" && row.session_mode === "device") this.openPasscode(row);
			else if (cmd === "delete") this.remove(row);
		},
		openForm(row) {
			this.form = row
				? {
					id: row.id,
					code: row.code,
					name: row.name,
					status: row.status,
					is_test: !!row.is_test,
					note: row.note || "",
					bill_count: row.bill_count || 0,
					lock_idle_seconds: posLockIdleValid(row.lock_idle_seconds) ? row.lock_idle_seconds : 300
				}
				: emptyForm();
			this.formVisible = true;
		},
		async save() {
			if (!this.formValid) return;
			this.saving = true;
			const body = {
				code: this.form.code,
				name: this.form.name.trim(),
				status: this.form.status,
				is_test: this.form.is_test,
				note: this.form.note,
				lock_idle_seconds: Number(this.form.lock_idle_seconds)
			};
			try {
				if (this.form.id) await PosService.PosDeviceUpdate(this.form.id, body);
				else await PosService.PosDeviceCreate(body);
				this.formVisible = false;
				this.posToast("pos.common.saved");
				this.load();
				this.$store.dispatch("Pos/fetchLookups", true).catch(() => {});
			} catch (err) {
				this.posError(err);
			} finally {
				this.saving = false;
			}
		},
		async pair(row) {
			try {
				const res = await PosService.PosDevicePairingCode(row.id);
				const d = (res.data && res.data.Datum) || {};
				this.pairing = { code: d.pairing_code, expiresAt: d.expires_at, deviceCode: row.code };
				this.tick();
				this.stopTimer();
				this.timer = setInterval(this.tick, 1000);
				this.pairVisible = true;
			} catch (err) {
				this.posError(err);
			}
		},
		tick() {
			this.countdown = posCountdown(this.pairing.expiresAt);
		},
		stopTimer() {
			if (this.timer) clearInterval(this.timer);
			this.timer = null;
		},
		async unpair(row) {
			if (!(await this.posConfirm(this.$t("pos.device.confirm_unpair", { code: row.code })))) return;
			try {
				await PosService.PosDeviceUnpair(row.id);
				this.posToast("pos.device.unpaired_ok");
				this.load();
			} catch (err) {
				this.posError(err);
			}
		},
		async remove(row) {
			if (!(await this.posConfirm(this.$t("pos.device.confirm_delete", { code: row.code })))) return;
			try {
				await PosService.PosDeviceDelete(row.id);
				this.posToast("pos.common.deleted");
				this.load();
			} catch (err) {
				this.posError(err);
			}
		},
		async openDefaults(row) {
			try {
				const p = await PosService.PosDeviceProductsGet(row.id);
				this.defaults = { deviceId: row.id, deviceCode: row.code, products: (p.data && p.data.Data) || [] };
				this.defaultsVisible = true;
			} catch (err) {
				this.posError(err);
			}
		},
		addDefaultProduct(p) {
			if (this.defaults.products.some((x) => x.pos_product_id === p.id)) return;
			this.defaults.products.push({ pos_product_id: p.id, name: p.name, code: p.code, price: p.price, status: p.status });
		},
		move(list, i, dir) {
			const j = i + dir;
			if (j < 0 || j >= list.length) return;
			const tmp = list[i];
			this.$set(list, i, list[j]);
			this.$set(list, j, tmp);
		},
		openPasscode(row) {
			this.passcode = { deviceId: row.id, deviceCode: row.code };
			this.passcodeVisible = true;
		},
		async savePasscode() {
			const p = this.passcode;
			this.saving = true;
			try {
				await PosService.devicePasscodeReset(p.deviceId, {});
				this.passcodeVisible = false;
				swal.fire({ title: this.$t("pos.device.passcode_reset_done"), text: this.$t("pos.device.passcode_reset_ok", { code: p.deviceCode }), icon: "success" });
				this.load();
			} catch (err) {
				this.posError(err);
			} finally {
				this.saving = false;
			}
		},
		async openStaff(row) {
			try {
				const res = await PosService.PosDeviceStaffGet(row.id);
				const d = (res.data && res.data.Datum) || {};
				this.staff = { deviceId: row.id, deviceCode: row.code, userIds: d.user_ids || [], candidates: d.candidates || [] };
				this.staffVisible = true;
			} catch (err) {
				this.posError(err);
			}
		},
		async saveStaff() {
			this.saving = true;
			try {
				await PosService.PosDeviceStaffPut(this.staff.deviceId, { user_ids: this.staff.userIds });
				this.staffVisible = false;
				this.posToast("pos.common.saved");
				this.load();
			} catch (err) {
				this.posError(err);
			} finally {
				this.saving = false;
			}
		},
		async saveDefaults() {
			this.saving = true;
			try {
				await PosService.PosDeviceProductsPut(this.defaults.deviceId, { pos_product_ids: this.defaults.products.map((p) => p.pos_product_id) });
				this.defaultsVisible = false;
				this.posToast("pos.common.saved");
			} catch (err) {
				this.posError(err);
			} finally {
				this.saving = false;
			}
		}
	}
};
</script>

<style>
.pos-apk-qr {
	width: 260px;
	height: 260px;
	max-width: 100%;
}
.pos-apk-url {
	word-break: break-all;
	user-select: all;
	font-size: 13px;
}
.pos-apk-copy {
	white-space: nowrap;
	flex-shrink: 0;
}
.pos-apk-hint {
	white-space: normal;
	word-break: normal;
}
.pos-tag-wrap.el-tag {
	white-space: normal;
	height: auto;
	line-height: 1.4;
	padding: 2px 8px;
	max-width: 100%;
}
.pos-nowrap .cell {
	white-space: nowrap;
}
.pos-pair-code {
	font: 700 48px/1.2 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
	letter-spacing: 0.15em;
	margin: 0.5rem 0 0.75rem;
}
</style>
